import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useState } from 'react'
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  FieldError,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@repo/ui'
import { ChevronDown, EllipsisVertical } from 'lucide-react'
import { z } from 'zod'
import type { OrgUser } from '@/api/org/users'
import { inviteUser, removeUser } from '@/api/org/users'
import { demoteAdmin, promoteAdmin } from '@/api/org/admin'
import { demoteOwner, promoteOwner } from '@/api/org/owner'
import type { UserOrgProfile } from '@/api/user/profile'
import { zodFieldValidator } from '@/common/parseDataType'
import { SettingsSection } from './ScopedSettings'
import type { ChangeEvent } from 'react'

// UX-level validators for the invite form. The generated InviteUserRequest schema
// is shape-only (no email-format / min-length), so these value constraints live
// here on the form rather than in the API contract.
const inviteFormSchema = z.object({
  first_name: z.string().min(2, 'Please provide a valid first name'),
  surname: z.string().min(2, 'Please provide a valid surname').optional(),
  email: z.email('Please enter a valid email address')
})

type RoleChange = {
  run: () => Promise<void>
  apply: (user: OrgUser) => OrgUser
}

function summariseUsers(users: Array<OrgUser>): string {
  const admins = users.filter((user) => user.admin).length
  const pending = users.filter((user) => !user.lastLoggedIn).length
  const parts = [
    `${users.length} ${users.length === 1 ? 'person' : 'people'}`,
    `${admins} ${admins === 1 ? 'admin' : 'admins'}`
  ]
  if (pending > 0) parts.push(`${pending} ${pending === 1 ? 'invite' : 'invites'} outstanding`)
  return parts.join(' \u00b7 ')
}

function formatLastSeen(dateStr: string | undefined): string {
  if (!dateStr) return 'never'
  const date = new Date(dateStr)
  if (!Number.isFinite(date.getTime())) return 'unknown'
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

export function OrganisationUsersSettings({
  users,
  userOrgProfile
}: {
  users: Array<OrgUser>
  userOrgProfile: UserOrgProfile | undefined
}) {
  const router = useRouter()

  const isAdmin = userOrgProfile?.org.admin === true
  const isOwner = userOrgProfile?.org.owner === true
  const currentUserId = userOrgProfile?.uid

  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<number | null>(null)
  const [editUser, setEditUser] = useState<OrgUser | null>(null)
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<string | null>(null)

  async function handleMutation(key: string, fn: () => Promise<void>) {
    setPendingAction(key)
    setMutationError(null)
    try {
      await fn()
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { userId: currentUserId } })
      setMutationError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setPendingAction(null)
    }
  }

  return (
    <>
      <SettingsSection
        id="organisation-users-heading"
        title="Users"
        {...(users.length > 0 ? { summary: summariseUsers(users) } : {})}
        action={
          isAdmin ? (
            <Button size="sm" className="shrink-0" onClick={() => setShowInviteDialog(true)}>
              Invite user
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Contact an admin to manage users.</p>
          )
        }
      >
        {mutationError && (
          <p role="alert" className="text-sm text-destructive">
            {mutationError}
          </p>
        )}

        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No users found.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="[&_th]:h-9 [&_th]:px-3 [&_th]:text-xs [&_th]:text-muted-foreground">
                  <TableHead colSpan={2}>Person</TableHead>
                  <TableHead>Membership</TableHead>
                  <TableHead>Permission</TableHead>
                  <TableHead>Last seen</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <UserRow
                    key={user.userId}
                    user={user}
                    currentUserId={currentUserId}
                    isAdmin={isAdmin}
                    onEdit={() => setEditUser(user)}
                    onRemove={() => setConfirmRemoveUserId(user.userId)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SettingsSection>

      <EditUserDialog
        key={editUser?.userId}
        user={editUser}
        isAdmin={isAdmin}
        isOwner={isOwner}
        isPending={pendingAction?.startsWith('edit-') ?? false}
        onSave={async (newOwner, newAdmin) => {
          if (!editUser) return
          const userId = editUser.userId
          const changes: Array<RoleChange> = []
          if (newOwner !== editUser.owner) {
            changes.push({
              run: newOwner ? () => promoteOwner(userId) : () => demoteOwner(userId),
              apply: (user) => ({ ...user, owner: newOwner })
            })
          }
          if (newAdmin !== editUser.admin) {
            changes.push({
              run: newAdmin ? () => promoteAdmin(userId) : () => demoteAdmin(userId),
              apply: (user) => ({ ...user, admin: newAdmin })
            })
          }
          setPendingAction(`edit-${userId}`)
          try {
            let acknowledged = editUser
            for (const change of changes) {
              await change.run()
              acknowledged = change.apply(acknowledged)
              setEditUser(acknowledged)
            }
            setEditUser(null)
          } finally {
            await router.invalidate()
            setPendingAction(null)
          }
        }}
        onClose={() => setEditUser(null)}
      />

      <RemoveUserDialog
        open={typeof confirmRemoveUserId === 'number'}
        isPending={pendingAction === `remove-${confirmRemoveUserId}`}
        onConfirm={() =>
          handleMutation(`remove-${confirmRemoveUserId}`, async () => {
            await removeUser(confirmRemoveUserId!)
            setConfirmRemoveUserId(null)
          })
        }
        onCancel={() => setConfirmRemoveUserId(null)}
      />

      <InviteUserDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onSuccess={async () => {
          setShowInviteDialog(false)
          await router.invalidate()
        }}
      />
    </>
  )
}

function UserRow({
  user,
  currentUserId,
  isAdmin,
  onEdit,
  onRemove
}: {
  user: OrgUser
  currentUserId: number | undefined
  isAdmin: boolean
  onEdit: () => void
  onRemove: () => void
}) {
  const isSelf = user.userId === currentUserId
  const initials = `${user.firstName.charAt(0)}${user.surname.charAt(0)}`.toUpperCase()

  return (
    <TableRow className="[&_td]:p-3 [&_td]:align-middle">
      <TableCell className="w-10">
        <Avatar className="size-8">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
      </TableCell>
      <TableCell className="w-full min-w-48 pl-1!">
        <div className="flex flex-col">
          <span className="font-medium">
            {user.firstName} {user.surname}
          </span>
          <span className="text-xs text-muted-foreground">{user.email}</span>
        </div>
      </TableCell>
      <TableCell>
        <MembershipBadge user={user} />
      </TableCell>
      <TableCell>
        <Badge variant="outline">{user.admin ? 'Admin' : 'Editor'}</Badge>
      </TableCell>
      <TableCell className="text-xs text-subtle-foreground tabular-nums">{formatLastSeen(user.lastLoggedIn)}</TableCell>
      <TableCell>
        {isAdmin && !isSelf && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="User actions">
                <EllipsisVertical className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onEdit}>Edit user</DropdownMenuItem>
              <DropdownMenuItem onClick={onRemove}>Remove user</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}

function MembershipBadge({ user }: { user: OrgUser }) {
  if (!user.lastLoggedIn)
    return (
      <Badge variant="outline" className="border-warning/30 text-warning">
        Invited
      </Badge>
    )
  if (user.owner)
    return (
      <Badge variant="outline" className="border-primary/30 bg-accent text-primary">
        Owner
      </Badge>
    )
  return <Badge variant="outline">Member</Badge>
}

function RoleDropdown({
  value,
  disabled,
  onChange,
  options
}: {
  value: string
  disabled: boolean
  onChange: (value: string) => void
  options: Array<{ value: string; label: string; description: string }>
}) {
  const selected = options.find((o) => o.value === value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="w-full justify-between">
          <span>{selected?.label ?? ''}</span>
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-(--radix-dropdown-menu-trigger-width)">
        {options.map((option) => (
          <DropdownMenuItem key={option.value} onClick={() => onChange(option.value)}>
            <div>
              <strong className="text-sm">{option.label}</strong>
              <p className="text-xs text-muted-foreground">{option.description}</p>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function EditUserDialog({
  user,
  isAdmin,
  isOwner,
  isPending,
  onSave,
  onClose
}: {
  user: OrgUser | null
  isAdmin: boolean
  isOwner: boolean
  isPending: boolean
  onSave: (newOwner: boolean, newAdmin: boolean) => Promise<void>
  onClose: () => void
}) {
  const [ownerValue, setOwnerValue] = useState<string>(user ? (user.owner ? 'owner' : 'member') : '')
  const [adminValue, setAdminValue] = useState<string>(user ? (user.admin ? 'admin' : 'editor') : '')
  const [error, setError] = useState<string | null>(null)

  async function handleSave() {
    if (!user) return
    setError(null)
    try {
      await onSave(ownerValue === 'owner', adminValue === 'admin')
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  return (
    <Dialog
      open={!!user}
      onOpenChange={(o: boolean) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{user ? `${user.firstName} ${user.surname}`.trim() : ''}</DialogDescription>
        </DialogHeader>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="space-y-4">
          {isOwner && (
            <div className="space-y-1.5">
              <Label>Membership</Label>
              <RoleDropdown
                value={ownerValue}
                disabled={isPending}
                onChange={setOwnerValue}
                options={[
                  { value: 'owner', label: 'Owner', description: 'Can manage the organisation' },
                  { value: 'member', label: 'Member', description: 'Member of the organisation' }
                ]}
              />
            </div>
          )}

          {isAdmin && (
            <div className="space-y-1.5">
              <Label>Role</Label>
              <RoleDropdown
                value={adminValue}
                disabled={isPending}
                onChange={setAdminValue}
                options={[
                  { value: 'admin', label: 'Admin', description: 'Editor + manage users and settings' },
                  { value: 'editor', label: 'Editor', description: 'Can manage content and use agents' }
                ]}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RemoveUserDialog({
  open,
  isPending,
  onConfirm,
  onCancel
}: {
  open: boolean
  isPending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) onCancel()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove user</DialogTitle>
          <DialogDescription className="sr-only">Remove this user from the organisation</DialogDescription>
        </DialogHeader>
        <p className="text-sm">Are you sure you want to remove this user? This action cannot be undone.</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Removing...' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function InviteUserDialog({
  open,
  onClose,
  onSuccess
}: {
  open: boolean
  onClose: () => void
  onSuccess: () => Promise<void>
}) {
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm({
    defaultValues: { first_name: '', surname: '', email: '' },
    onSubmit: async ({ value }) => {
      setSubmitError(null)
      try {
        await inviteUser({
          first_name: value.first_name,
          surname: value.surname || undefined,
          email: value.email
        })
        form.reset()
        await onSuccess()
      } catch (err) {
        Sentry.captureException(err)
        setSubmitError(err instanceof Error ? err.message : 'An error occurred')
      }
    }
  })

  function handleClose() {
    form.reset()
    setSubmitError(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o: boolean) => {
        if (!o) handleClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
          <DialogDescription className="sr-only">Invite a new user to the organisation</DialogDescription>
        </DialogHeader>

        {submitError && (
          <p role="alert" className="text-sm text-destructive">
            {submitError}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="first_name" validators={{ onChange: zodFieldValidator(inviteFormSchema.shape.first_name) }}>
            {(field) => (
              <Field>
                <Input
                  placeholder="First Name"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Field name="surname" validators={{ onChange: zodFieldValidator(inviteFormSchema.shape.surname) }}>
            {(field) => (
              <Field>
                <Input
                  placeholder="Surname"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <form.Field name="email" validators={{ onChange: zodFieldValidator(inviteFormSchema.shape.email) }}>
            {(field) => (
              <Field>
                <Input
                  placeholder="Email address"
                  type="email"
                  value={field.state.value}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={form.state.isSubmitting}
                />
                <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
              </Field>
            )}
          </form.Field>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={form.state.isSubmitting}>
              Close
            </Button>
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Invite'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
